'use client'

import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getPromptVersions } from '@/lib/vault/prompts/version-history-actions'
import { useSseChannel } from '@/lib/sse/use-sse-channel'
import type { PromptVersion } from '@/types/prompt.types'

interface UseVersionHistoryOptions {
  promptId: string
  branch?: string
  initialData?: PromptVersion[]
}

export function useVersionHistory({
  promptId,
  branch,
  initialData,
}: UseVersionHistoryOptions) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['versions', promptId, branch],
    queryFn: async (): Promise<PromptVersion[]> => {
      const result = await getPromptVersions(promptId, branch)
      if (!result.success) throw new Error(result.error)
      return result.data ?? []
    },
    initialData,
  })

  // Realtime: invalidate the version list when a new version is
  // inserted for this prompt. Trigger payload doesn't include the
  // full row, so the next refetch is authoritative.
  const onVersionEvent = useCallback(
    (event: { type: string }) => {
      if (event.type !== 'message') return
      queryClient.invalidateQueries({ queryKey: ['versions', promptId, branch] })
    },
    [queryClient, promptId, branch],
  )

  useSseChannel(
    promptId ? `prompt:${promptId}:versions` : null,
    onVersionEvent,
    { enabled: !!promptId },
  )

  return query
}
